/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import type {
    DmlsOptions,
    VinylErrorLogger,
    VinylErrorLoggingAuthProvider,
    VinylErrorLoggingOptions,
} from '@amzn/vinyl-amzn'
import { globalRef } from '@amazon/vinyl-util'
import { data, type MutableValue } from '@amazon/vinyl-observable'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { selectErrorLoggingEndpoint } from './errorLoggingEndpoint'

/**
 * The Vinyl CloudWatch logger stream an app-wide {@link VinylErrorLogger} posts to:
 * - 'error' — engine errors, injected into every player.
 * - 'app' — errors the web app itself catches or lets escape, with their own budget so engine
 *   errors and app errors can't starve each other.
 * - 'feedback' — Help page feedback, with its own `maxMessages` budget so an error burst
 *   can't starve a user's report.
 */
export type VinylLoggerKind = 'error' | 'app' | 'feedback'

interface VinylLoggers {
    // Kept current by the player auth binding so every log carries the signed-in customer.
    readonly dmls$: MutableValue<DmlsOptions | null>
    // The in-flight build is shared so concurrent first callers get one logger.
    readonly loggers: Partial<Record<VinylLoggerKind, Promise<VinylErrorLogger>>>
}

const vinylLoggersRef = globalRef<VinylLoggers>(() => ({
    dmls$: data<DmlsOptions | null>(null),
    loggers: {},
}))

/** The DMLS options every Vinyl logger authenticates with. */
export function getVinylLoggerDmls(): MutableValue<DmlsOptions | null> {
    return vinylLoggersRef.value.dmls$
}

/**
 * The app-wide Vinyl CloudWatch logger of the given kind, built on first use. A failed build is
 * forgotten so the next caller retries it.
 */
export function getVinylLogger(kind: VinylLoggerKind): Promise<VinylErrorLogger> {
    const { loggers } = vinylLoggersRef.value
    return (loggers[kind] ??= createVinylLogger().catch((error: unknown) => {
        delete loggers[kind]
        throw error
    }))
}

// Authenticates from the session itself rather than the player's DMLS options, which only exist
// once a player is built: an app error before the first playback must still be reportable.
function sessionAuthProvider(): VinylErrorLoggingAuthProvider {
    return async () => {
        const { auth, device } = getPlaybackEnv()
        const { deviceId, deviceTypeId } = device
        if (!auth.usesBearer()) {
            return { oAuthToken: null, deviceId, deviceTypeId, useAuthCookie: true }
        }
        return { oAuthToken: await auth.authTokenProvider(), deviceId, deviceTypeId }
    }
}

// Imports vinyl-amzn dynamically to keep it off the eager boot chunk.
async function createVinylLogger(): Promise<VinylErrorLogger> {
    const vinyl = await import('@amzn/vinyl-amzn')
    const { auth, endpoints } = getPlaybackEnv()
    return new vinyl.VinylErrorLogger({
        options: data<Partial<VinylErrorLoggingOptions>>({
            endpoint: selectErrorLoggingEndpoint({
                cookieAuth: !auth.usesBearer(),
                pageOrigin: endpoints.pageOrigin(),
                defaultEndpoint: vinyl.VinylErrorLoggingEndpoints.VINYL_DEV,
            }),
        }),
        authProvider: sessionAuthProvider(),
    })
}
